/**
 * LivingGlobe.jsx
 *
 * Feb 11 2026 · The centrepiece of One Million Lights.
 *
 * A cinematic three.js globe that renders every verified light in
 * warm gold, and every fresh contribution as a brilliant white pulse
 * that fades to gold after ~10 seconds ("this light is yours" →
 * "now it belongs to all of us").
 *
 * · WebGL point-cloud via `react-globe.gl` — handles 1M+ points.
 * · Deep-navy near-black background, subtle atmospheric glow.
 * · Auto-rotates gently when untouched; pauses on drag.
 * · Static SVG fallback for prefers-reduced-motion + old devices.
 * · No precise personal coordinates ever rendered — only jittered
 *   points around country centroids.
 *
 * Backend: GET /api/one-million-lights/globe-data
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Globe from "react-globe.gl";
import * as THREE from "three";

const API = process.env.REACT_APP_BACKEND_URL;

const TOK = {
  gold: "#e0a97a",
  goldWarm: "#f4d59f",
  cream: "#f5ecd9",
  creamMist: "rgba(245,236,217,0.42)",
  goldSoft: "rgba(224,169,122,0.86)",
  navyDeep: "#050b1a",
  navyGlow: "rgba(80,110,180,0.10)",
};

export default function LivingGlobe({
  height = 480,
  freshLight = null,     // { lat, lng, count } — a new white light to pulse
  onFreshFaded,          // called when the white pulse has faded to gold
  onCountryTap,          // called with country object when a country is tapped
  reduceMotion = false,
}) {
  const containerRef = useRef(null);
  const globeRef = useRef(null);
  const [width, setWidth] = useState(360);
  const [data, setData] = useState(null);
  const [webglOk, setWebglOk] = useState(true);
  const [hoverCountry, setHoverCountry] = useState(null);
  // Fresh-light lifecycle: null → white → fading → gold. Held for
  // ~10s, then a callback fires so the parent can reveal share CTAs.
  const [pulsePhase, setPulsePhase] = useState("idle");

  // Detect WebGL — fall back to a static plate only when the
  // browser truly cannot render a canvas (rare — mainly in-app
  // browsers with WebGL disabled). Reduced-motion is respected via
  // the globe's autoRotate flag below, NOT by killing WebGL, so
  // the user still gets a beautiful static globe they can drag.
  useEffect(() => {
    try {
      const c = document.createElement("canvas");
      const gl = c.getContext("webgl") || c.getContext("experimental-webgl");
      setWebglOk(!!gl);
    } catch { setWebglOk(false); }
  }, []);

  // Resize globe with its container. Debounced via rAF.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    let raf = 0;
    const onSize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setWidth(Math.max(280, el.clientWidth));
      });
    };
    onSize();
    const ro = new ResizeObserver(onSize);
    ro.observe(el);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); };
  }, []);

  // Load aggregated globe data once — polls every 30 s to keep the
  // counter and per-country totals fresh without hammering the API.
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/one-million-lights/globe-data`, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        // Silent — the fallback still shows an accurate live counter.
        console.warn("[globe] load failed:", e?.message);
      }
      if (!cancelled) timer = setTimeout(load, 30000);
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  // Configure the globe once it mounts — gentle auto-rotate, no zoom
  // on scroll, subtle atmosphere. Pause rotation on interaction.
  useEffect(() => {
    if (!globeRef.current || !webglOk) return;
    const g = globeRef.current;
    try {
      const controls = g.controls();
      controls.autoRotate = !reduceMotion;
      controls.autoRotateSpeed = 0.28;
      controls.enableZoom = false;   // keep it emotional, not exploratory
      controls.enablePan = false;
      controls.rotateSpeed = 0.6;
      // Pause auto-rotation while the user drags.
      controls.addEventListener("start", () => { controls.autoRotate = false; });
      controls.addEventListener("end", () => {
        setTimeout(() => { if (globeRef.current) controls.autoRotate = !reduceMotion; }, 1500);
      });
    } catch (e) { /* controls not ready yet */ }
    // Camera height — pulled back a bit so the whole globe is visible.
    try { g.pointOfView({ altitude: 2.4 }, 0); } catch { /* noop */ }
  }, [webglOk, reduceMotion]);

  // Fresh-light lifecycle: rotate to region → white pulse → fade to
  // gold after 10s. This is the emotional payoff after checkout.
  useEffect(() => {
    if (!freshLight || !globeRef.current || !webglOk) return;
    setPulsePhase("white");
    try {
      globeRef.current.pointOfView(
        { lat: freshLight.lat, lng: freshLight.lng, altitude: 1.9 },
        1800,
      );
    } catch { /* noop */ }
    const toFade = setTimeout(() => setPulsePhase("fading"), 9000);
    const toGold = setTimeout(() => {
      setPulsePhase("gold");
      onFreshFaded?.();
    }, 11000);
    return () => { clearTimeout(toFade); clearTimeout(toGold); };
  }, [freshLight, webglOk, onFreshFaded]);

  // Merge existing gold points with the fresh white pulse point.
  const pointsData = useMemo(() => {
    const base = data?.points || [];
    if (!freshLight) return base;
    const fresh = {
      lat: freshLight.lat,
      lng: freshLight.lng,
      size: 0.5,
      n: freshLight.count || 1,
      __fresh: true,
      __phase: pulsePhase,
    };
    return [...base, fresh];
  }, [data, freshLight, pulsePhase]);

  // Per-point colour — white while fresh, gold otherwise. Height
  // encodes cluster size so busier regions rise slightly off the
  // surface (very subtle, ~0-0.03 of Earth radius).
  const pointColor = (d) => {
    if (d.__fresh) {
      if (d.__phase === "white") return "#ffffff";
      if (d.__phase === "fading") return "#fce6c9";
      return TOK.goldWarm;
    }
    return TOK.gold;
  };
  const pointAltitude = (d) => {
    if (d.__fresh) return 0.06;
    return Math.min(0.03, (d.n || 1) / 600);
  };
  const pointRadius = (d) => {
    if (d.__fresh) {
      if (d.__phase === "white") return 1.1;
      if (d.__phase === "fading") return 0.75;
      return Math.max(0.42, d.size * 1.6);
    }
    // Feb 22 2026 · Michael's mobile review — dots were near-invisible
    // at phone width. Boost to a floor of 0.28 with a mild growth on
    // busier clusters so mobile still shows the movement's presence.
    return Math.max(0.28, d.size * 1.6);
  };

  // Country-tap rings so a tap on a light region shows a label.
  const ringsData = useMemo(() => {
    if (freshLight) {
      return [{
        lat: freshLight.lat,
        lng: freshLight.lng,
        maxR: pulsePhase === "gold" ? 0 : 5,
        propagationSpeed: 3,
        repeatPeriod: 1800,
      }];
    }
    if (hoverCountry) {
      return [{
        lat: hoverCountry.lat,
        lng: hoverCountry.lng,
        maxR: 3,
        propagationSpeed: 2,
        repeatPeriod: 3000,
      }];
    }
    return [];
  }, [freshLight, pulsePhase, hoverCountry]);

  const display = data?.total ?? 0;
  const countryCount = data?.countries_count ?? 0;
  const progressPct = ((data?.progress || 0) * 100);

  return (
    <div
      ref={containerRef}
      className="relative w-full flex flex-col"
      style={{ height, background: TOK.navyDeep }}
      data-testid="oml-living-globe"
    >
      {/* Live counter — reserved band ABOVE the sphere so nothing
          overlaps. Feb 11 2026 · Michael reported the counter
          overlapping the northern gold cluster. */}
      {webglOk && (
        <div
          className="pointer-events-none flex flex-col items-center pt-2 pb-1 shrink-0"
          data-testid="oml-globe-counter"
        >
          <div
            className="tabular-nums"
            style={{
              color: TOK.cream,
              fontFamily: '"Manrope", -apple-system, sans-serif',
              fontSize: "clamp(1.5rem, 5vw, 2.1rem)",
              fontWeight: 300,
              letterSpacing: "-0.01em",
              textShadow: "0 0 30px rgba(224,169,122,0.35)",
              lineHeight: 1.1,
            }}
          >
            {display.toLocaleString("en-GB")}
          </div>
          <div
            className="mt-0.5 uppercase"
            style={{
              color: TOK.goldSoft,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.55rem",
              letterSpacing: "0.32em",
            }}
          >
            lights across {countryCount} countries
          </div>
        </div>
      )}

      {/* Globe body — takes remaining vertical space. */}
      <div className="relative flex-1 min-h-0" style={{ minHeight: 200 }}>
      {webglOk ? (
        <Globe
          ref={globeRef}
          width={width}
          height={Math.max(200, height - 90)}
          backgroundColor="rgba(0,0,0,0)"
          // Feb 22 2026 · Michael's mobile review — the globe was
          // rendering as a faint blue circle because the sphere was
          // deep navy against a near-black background, with no
          // continents. Layer a low-res Earth night texture on top
          // of our own tinted material so continents are legible
          // and warm-gold-lit while keeping the Soulumo palette.
          globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
          showAtmosphere
          atmosphereColor="#7fa8ff"
          atmosphereAltitude={0.16}
          globeMaterial={new THREE.MeshPhongMaterial({
            color: "#1a3260",
            emissive: "#0a1a3a",
            emissiveIntensity: 0.28,
            shininess: 8,
            transparent: true,
            opacity: 1,
          })}
          hexPolygonsData={[]}
          pointsData={pointsData}
          pointLat="lat"
          pointLng="lng"
          pointColor={pointColor}
          pointAltitude={pointAltitude}
          pointRadius={pointRadius}
          pointsMerge={false}
          pointResolution={5}
          onPointClick={(d) => {
            if (d.__fresh) return;
            const c = data?.countries?.find((cc) => cc.code === d.cc);
            if (c) {
              setHoverCountry(c);
              onCountryTap?.(c);
            }
          }}
          ringsData={ringsData}
          ringColor={() => (freshLight ? "rgba(255,255,255,0.7)" : TOK.goldSoft)}
          ringMaxRadius="maxR"
          ringPropagationSpeed="propagationSpeed"
          ringRepeatPeriod="repeatPeriod"
        />
      ) : (
        <StaticFallback total={display} countryCount={countryCount} />
      )}
      </div>

      {/* Progress line — thin warm gold, at the very bottom of the
          globe wrapper (below the sphere, above the primary CTA). */}
      <div
        className="flex flex-col items-center pt-1 pb-2 pointer-events-none shrink-0"
        data-testid="oml-globe-progress"
      >
        <div
          className="w-4/5 h-[2px] rounded-full overflow-hidden"
          style={{ background: "rgba(224,169,122,0.14)" }}
        >
          <div
            className="h-full"
            style={{
              width: `${Math.max(1.2, progressPct)}%`,
              background: `linear-gradient(90deg, ${TOK.gold} 0%, ${TOK.goldWarm} 100%)`,
              boxShadow: `0 0 14px ${TOK.goldSoft}`,
            }}
          />
        </div>
        <div
          className="mt-1"
          style={{
            color: TOK.creamMist,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.52rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
          }}
        >
          {display.toLocaleString("en-GB")} of 1,000,000
        </div>
      </div>

      {/* Country tap label — quiet, fades in and out. */}
      {hoverCountry && (
        <div
          className="absolute top-16 left-1/2 -translate-x-1/2 rounded-2xl px-4 py-3 text-center"
          style={{
            background: "rgba(6,10,22,0.72)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            border: `1px solid ${TOK.goldSoft}`,
            color: TOK.cream,
            fontFamily: '"Cormorant Garamond", serif',
            animation: "oml-fade 320ms ease",
          }}
          data-testid="oml-globe-country-label"
        >
          <div style={{ fontSize: "1.1rem", fontStyle: "italic" }}>{hoverCountry.name}</div>
          <div
            className="mt-0.5 tabular-nums"
            style={{
              color: TOK.gold,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.72rem",
              letterSpacing: "0.16em",
            }}
          >
            {hoverCountry.count.toLocaleString("en-GB")} lights
          </div>
          <button
            type="button"
            onClick={() => setHoverCountry(null)}
            className="mt-1 text-[0.5rem] uppercase tracking-[0.28em]"
            style={{ color: TOK.creamMist }}
          >
            Dismiss
          </button>
        </div>
      )}

      <style>{`
        @keyframes oml-fade {
          from { opacity: 0; transform: translate(-50%, -6px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
}

/* ─── Static SVG fallback ──────────────────────────────────────────
 * Rendered when WebGL is unavailable OR prefers-reduced-motion is on.
 * No animation. Just a flat golden aggregation of the total, with a
 * decorative dot-grid so the section still reads as premium.
 */
function StaticFallback({ total, countryCount }) {
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <div
        aria-hidden
        className="absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(circle at 30% 40%, rgba(224,169,122,0.35) 0%, transparent 45%), radial-gradient(circle at 70% 60%, rgba(80,110,180,0.20) 0%, transparent 55%)",
        }}
      />
      <div className="relative text-center px-6">
        <div
          className="italic mb-3"
          style={{ color: "rgba(245,236,217,0.6)", fontFamily: '"Cormorant Garamond", serif', fontSize: "0.98rem" }}
        >
          The living globe rests. The lights are still shining.
        </div>
        <div
          className="tabular-nums"
          style={{
            color: TOK.cream,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "2.4rem",
            fontWeight: 300,
            textShadow: "0 0 30px rgba(224,169,122,0.4)",
          }}
        >
          {total.toLocaleString("en-GB")}
        </div>
        <div
          className="mt-2 uppercase"
          style={{
            color: TOK.goldSoft,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.58rem",
            letterSpacing: "0.32em",
          }}
        >
          lights across {countryCount} countries
        </div>
      </div>
    </div>
  );
}
